import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExitIntentPopup() {
  const [promo, setPromo] = useState(null);
  const [show, setShow] = useState(false);
  const dismissed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const { data } = await axios.get(`${API}/promotions/active`);
        const popup = data.find((p) => p.type === "exit_popup");
        if (popup) setPromo(popup);
      } catch (_e) { /* silently ignore */ }
    };
    fetchPromo();
  }, []);

  useEffect(() => {
    if (!promo) return;
    const alreadyShown = sessionStorage.getItem("exit_popup_shown");
    if (alreadyShown) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !dismissed.current) {
        setShow(true);
        sessionStorage.setItem("exit_popup_shown", "1");
      }
    };

    // Also trigger after 30 seconds of inactivity
    const timer = setTimeout(() => {
      if (!dismissed.current && !sessionStorage.getItem("exit_popup_shown")) {
        setShow(true);
        sessionStorage.setItem("exit_popup_shown", "1");
      }
    }, 30000);

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(timer);
    };
  }, [promo]);

  const close = () => {
    setShow(false);
    dismissed.current = true;
  };

  if (!show || !promo) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 animate-in fade-in duration-300" data-testid="exit-intent-popup">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        <button onClick={close} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-md transition-colors" data-testid="exit-popup-close">
          <X className="w-4 h-4 text-slate-600" />
        </button>

        {promo.image_url && (
          <img src={promo.image_url} alt="" className="w-full h-40 object-cover" />
        )}

        <div className="p-6 text-center" style={{ background: promo.bg_color, color: promo.text_color }}>
          {promo.discount_text && (
            <p className="text-3xl font-black tracking-tight mb-1">{promo.discount_text}</p>
          )}
          <h3 className="text-xl font-bold">{promo.title}</h3>
        </div>

        <div className="p-6 text-center">
          {promo.description && <p className="text-slate-600 mb-4">{promo.description}</p>}
          <Button
            className="px-8 py-2.5 rounded-full font-semibold"
            style={{ background: promo.bg_color, color: promo.text_color }}
            onClick={() => { close(); navigate(promo.cta_link || "/get-quote"); }}
            data-testid="exit-popup-cta"
          >
            {promo.cta_text || "Get Offer"}
          </Button>
          <button onClick={close} className="block mx-auto mt-3 text-sm text-slate-400 hover:text-slate-600 transition-colors">
            No thanks, I&apos;ll pass
          </button>
        </div>
      </div>
    </div>
  );
}
